import React, { useState } from "react";
import { KeyRound } from "lucide-react";
import { theme } from "../styles/theme";
import { validarForcaSenha, SENHA_REQUISITOS_TEXTO } from "../utils/validarSenha";

interface ModalTrocarSenhaProps {
  onTrocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  onClose: () => void;
  isPrimeiroAcesso?: boolean;
}

export default function ModalTrocarSenha({
  onTrocarSenha,
  onClose,
  isPrimeiroAcesso = false,
}: ModalTrocarSenhaProps) {
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [mostrarSenhas, setMostrarSenhas] = useState(false);

  const validarSenha = (): boolean => {
    if (!senhaAtual.trim()) {
      setErro("Digite a senha atual");
      return false;
    }

    if (!novaSenha.trim()) {
      setErro("Digite a nova senha");
      return false;
    }

    const erroForca = validarForcaSenha(novaSenha);
    if (erroForca) {
      setErro(erroForca);
      return false;
    }

    if (novaSenha === senhaAtual) {
      setErro("A nova senha deve ser diferente da senha atual");
      return false;
    }

    if (novaSenha !== confirmarNovaSenha) {
      setErro("As senhas não coincidem");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");

    if (!validarSenha()) {
      return;
    }

    try {
      setLoading(true);
      await onTrocarSenha(senhaAtual, novaSenha);
      alert("✅ Senha alterada com sucesso!");
      onClose();
    } catch (error: any) {
      console.error("❌ Erro ao trocar senha:", error);

      // Mensagens de erro amigáveis
      if (error.code === "auth/wrong-password") {
        setErro("Senha atual incorreta");
      } else if (error.code === "auth/weak-password") {
        setErro("A nova senha é muito fraca");
      } else if (error.code === "auth/requires-recent-login") {
        setErro("Por segurança, faça login novamente antes de trocar a senha");
      } else {
        setErro(error.message || "Erro ao trocar senha");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    border: `1px solid ${theme.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: "14px",
    fontFamily: theme.fonts.sans,
    boxSizing: "border-box" as const,
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={isPrimeiroAcesso ? undefined : onClose} // Não fecha ao clicar fora se for primeiro acesso
    >
      <div
        style={{
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.xl,
          maxWidth: "450px",
          width: "100%",
          padding: "32px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* CABEÇALHO */}
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <KeyRound size={48} style={{ marginBottom: "12px" }} />
          <h2
            style={{
              fontSize: "22px",
              fontWeight: "bold",
              color: theme.gray[800],
              margin: "0 0 8px 0",
              fontFamily: theme.fonts.sans,
            }}
          >
            {isPrimeiroAcesso ? "Primeiro Acesso" : "Trocar Senha"}
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[600],
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            {isPrimeiroAcesso
              ? "Por segurança, troque sua senha temporária"
              : "Crie uma nova senha para sua conta"}
          </p>
        </div>

        {/* ALERTA PRIMEIRO ACESSO */}
        {isPrimeiroAcesso && (
          <div
            style={{
              background: "#fef3c7",
              border: `2px solid #f59e0b`,
              borderRadius: theme.borderRadius.md,
              padding: "12px 16px",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                fontSize: "13px",
                color: "#92400e",
                margin: 0,
                fontFamily: theme.fonts.sans,
              }}
            >
              ⚠️ <strong>Obrigatório:</strong> Você deve trocar a senha
              temporária antes de continuar.
            </p>
          </div>
        )}

        {/* FORMULÁRIO */}
        <form onSubmit={handleSubmit}>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {/* SENHA ATUAL */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                  marginBottom: "6px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Senha Atual *
              </label>
              <input
                type={mostrarSenhas ? "text" : "password"}
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                style={inputStyle}
                placeholder="Digite sua senha atual"
                disabled={loading}
              />
            </div>

            {/* NOVA SENHA */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                  marginBottom: "6px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Nova Senha *
              </label>
              <input
                type={mostrarSenhas ? "text" : "password"}
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                style={inputStyle}
                placeholder="Mínimo 12 caracteres"
                disabled={loading}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: theme.gray[500],
                  marginTop: "4px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {SENHA_REQUISITOS_TEXTO}
              </p>
            </div>

            {/* CONFIRMAR NOVA SENHA */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                  marginBottom: "6px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Confirmar Nova Senha *
              </label>
              <input
                type={mostrarSenhas ? "text" : "password"}
                value={confirmarNovaSenha}
                onChange={(e) => setConfirmarNovaSenha(e.target.value)}
                style={inputStyle}
                placeholder="Digite a senha novamente"
                disabled={loading}
              />
            </div>

            {/* MOSTRAR SENHAS */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              <input
                type="checkbox"
                checked={mostrarSenhas}
                onChange={(e) => setMostrarSenhas(e.target.checked)}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
                disabled={loading}
              />
              <span style={{ fontSize: "13px", color: theme.gray[600] }}>
                Mostrar senhas
              </span>
            </label>

            {/* ERRO */}
            {erro && (
              <div
                style={{
                  background: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: theme.borderRadius.md,
                  padding: "12px",
                }}
              >
                <p
                  style={{
                    color: "#c00",
                    fontSize: "13px",
                    margin: 0,
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  ❌ {erro}
                </p>
              </div>
            )}

            {/* BOTÕES */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                marginTop: "8px",
              }}
            >
              {!isPrimeiroAcesso && (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: theme.gray[200],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: theme.fonts.sans,
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  Cancelar
                </button>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: loading ? theme.gray[400] : theme.primary[600],
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                {loading ? "Alterando..." : "Confirmar Alteração"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
